/**
 * Component Registry
 *
 * Maps block keys to their React components and configuration schemas.
 * This is the central source of truth for all available page blocks.
 */

import React from 'react';
import type { BlockRegistryEntry, BlockComponentProps } from './types';

/* ------------------------------------------------------------------ */
/*  Lazy-loaded block components                                       */
/* ------------------------------------------------------------------ */
const HeroBlock = React.lazy(() => import('./blocks/HeroBlock'));
const ContentBlock = React.lazy(() => import('./blocks/ContentBlock'));
const StatsGridBlock = React.lazy(() => import('./blocks/StatsGridBlock'));
const FeatureGridBlock = React.lazy(() => import('./blocks/FeatureGridBlock'));
const StepsBlock = React.lazy(() => import('./blocks/StepsBlock'));
const FaqBlock = React.lazy(() => import('./blocks/FaqBlock'));
const GalleryBlock = React.lazy(() => import('./blocks/GalleryBlock'));
const InvestCtaBlock = React.lazy(() => import('./blocks/InvestCtaBlock'));
const SummaryCardBlock = React.lazy(() => import('./blocks/SummaryCardBlock'));
const RiskDisclosureBlock = React.lazy(() => import('./blocks/RiskDisclosureBlock'));
const SpacerBlock = React.lazy(() => import('./blocks/SpacerBlock'));
const TabsBlock = React.lazy(() => import('./blocks/TabsBlock'));
const DocumentsBlock = React.lazy(() => import('./blocks/DocumentsBlock'));
const TeamBlock = React.lazy(() => import('./blocks/TeamBlock'));
const TestimonialsBlock = React.lazy(() => import('./blocks/TestimonialsBlock'));
const ProgressBarBlock = React.lazy(() => import('./blocks/ProgressBarBlock'));
const MarkdownBlock = React.lazy(() => import('./blocks/MarkdownBlock'));
const VideoBlock = React.lazy(() => import('./blocks/VideoBlock'));
const FundTermsBlock = React.lazy(() => import('./blocks/FundTermsBlock'));
const CaseStudiesBlock = React.lazy(() => import('./blocks/CaseStudiesBlock'));
const YieldComparisonBlock = React.lazy(() => import('./blocks/YieldComparisonBlock'));
const LocationBlock = React.lazy(() => import('./blocks/LocationBlock'));
const ProjectionTableBlock = React.lazy(() => import('./blocks/ProjectionTableBlock'));
const AssumptionsBlock = React.lazy(() => import('./blocks/AssumptionsBlock'));
const NavbarBlock = React.lazy(() => import('./blocks/NavbarBlock'));
const CtaBannerBlock = React.lazy(() => import('./blocks/CtaBannerBlock'));
const PricingBlock = React.lazy(() => import('./blocks/PricingBlock'));
const ContactFormBlock = React.lazy(() => import('./blocks/ContactFormBlock'));
const LogoCloudBlock = React.lazy(() => import('./blocks/LogoCloudBlock'));
const NewsletterBlock = React.lazy(() => import('./blocks/NewsletterBlock'));
const FooterBlock = React.lazy(() => import('./blocks/FooterBlock'));

/* ------------------------------------------------------------------ */
/*  Registry: block key → { component, schema }                       */
/* ------------------------------------------------------------------ */

export type BlockComponent = React.ComponentType<BlockComponentProps<any>>;

interface RegistryItem {
  component: React.LazyExoticComponent<BlockComponent>;
  entry: BlockRegistryEntry;
}

const registry = new Map<string, RegistryItem>();

function register(entry: BlockRegistryEntry, component: React.LazyExoticComponent<BlockComponent>) {
  registry.set(entry.key, { component, entry });
}

/* ------------------------------------------------------------------ */
/*  Block Definitions                                                  */
/* ------------------------------------------------------------------ */

// ── HERO ────────────────────────────────────────────────────────────
register(
  {
    key: 'hero',
    label: 'Hero Banner',
    description: 'Full-width hero section with image, title, and subtitle',
    category: 'layout',
    icon: 'Image',
    maxPerPage: 1,
    defaultProps: {
      title: 'Investment Opportunity',
      subtitle: '',
      imageUrl: '',
      overlay: true,
      height: 'large',
    },
    schema: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'subtitle', label: 'Subtitle', type: 'textarea' },
      { key: 'imageUrl', label: 'Background Image URL', type: 'image' },
      { key: 'overlay', label: 'Dark Overlay', type: 'boolean', defaultValue: true },
      { key: 'height', label: 'Height', type: 'select', options: [
        { label: 'Small (280px)', value: 'small' },
        { label: 'Medium (400px)', value: 'medium' },
        { label: 'Large (500px)', value: 'large' },
      ], defaultValue: 'large' },
    ],
  },
  HeroBlock
);

// ── CONTENT ─────────────────────────────────────────────────────────
register(
  {
    key: 'content',
    label: 'Content Section',
    description: 'Rich text content block with optional heading',
    category: 'content',
    icon: 'FileText',
    defaultProps: {
      heading: '',
      body: '',
      centered: false,
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Body Content', type: 'richtext', required: true },
      { key: 'centered', label: 'Center Text', type: 'boolean', defaultValue: false },
    ],
  },
  ContentBlock
);

// ── MARKDOWN ────────────────────────────────────────────────────────
register(
  {
    key: 'markdown',
    label: 'Markdown Content',
    description: 'Render markdown with full formatting support',
    category: 'content',
    icon: 'Code',
    defaultProps: { content: '' },
    schema: [
      { key: 'content', label: 'Markdown Content', type: 'richtext', required: true, helpText: 'Supports full Markdown: headings, bold, lists, links, tables' },
    ],
  },
  MarkdownBlock
);

// ── STATS GRID ──────────────────────────────────────────────────────
register(
  {
    key: 'stats-grid',
    label: 'Stats / Metrics',
    description: 'Grid of key statistics and metrics',
    category: 'data',
    icon: 'BarChart3',
    defaultProps: {
      heading: 'Key Metrics',
      columns: 3,
      stats: [
        { label: 'Total Value', value: '$5,000,000' },
        { label: 'Target Return', value: '12%' },
        { label: 'Term', value: '5 Years' },
      ],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'columns', label: 'Columns', type: 'select', options: [
        { label: '2 Columns', value: '2' },
        { label: '3 Columns', value: '3' },
        { label: '4 Columns', value: '4' },
      ], defaultValue: '3' },
      { key: 'stats', label: 'Statistics', type: 'list', listSchema: [
        { key: 'label', label: 'Label', type: 'text', required: true },
        { key: 'value', label: 'Value', type: 'text', required: true },
      ] },
    ],
  },
  StatsGridBlock
);

// ── FEATURE GRID ────────────────────────────────────────────────────
register(
  {
    key: 'feature-grid',
    label: 'Feature Cards',
    description: 'Grid of icon + title + description cards',
    category: 'content',
    icon: 'Grid3X3',
    defaultProps: {
      heading: 'Investment Highlights',
      columns: 3,
      features: [
        { icon: 'TrendingUp', title: 'Strong Returns', description: 'Targeting above-market yields' },
        { icon: 'Shield', title: 'Asset Backed', description: 'Secured by real assets' },
        { icon: 'Award', title: 'Expert Management', description: 'Professional fund management' },
      ],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'columns', label: 'Columns', type: 'select', options: [
        { label: '2 Columns', value: '2' },
        { label: '3 Columns', value: '3' },
        { label: '4 Columns', value: '4' },
      ], defaultValue: '3' },
      { key: 'features', label: 'Features', type: 'list', listSchema: [
        { key: 'icon', label: 'Icon Name', type: 'text', helpText: 'Lucide icon name (e.g. TrendingUp, Shield, Award)', defaultValue: 'Star' },
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'description', label: 'Description', type: 'textarea' },
      ] },
    ],
  },
  FeatureGridBlock
);

// ── STEPS ───────────────────────────────────────────────────────────
register(
  {
    key: 'steps',
    label: 'Steps / How It Works',
    description: 'Numbered step cards',
    category: 'content',
    icon: 'ListOrdered',
    defaultProps: {
      heading: 'How It Works',
      steps: [
        { title: 'Step 1', description: 'Description' },
        { title: 'Step 2', description: 'Description' },
        { title: 'Step 3', description: 'Description' },
      ],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'steps', label: 'Steps', type: 'list', listSchema: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'description', label: 'Description', type: 'textarea', required: true },
      ] },
    ],
  },
  StepsBlock
);

// ── FAQ ─────────────────────────────────────────────────────────────
register(
  {
    key: 'faq',
    label: 'FAQ Accordion',
    description: 'Expandable question-and-answer section',
    category: 'interactive',
    icon: 'HelpCircle',
    defaultProps: {
      heading: 'Frequently Asked Questions',
      items: [
        { question: 'What is the minimum investment?', answer: 'The minimum investment varies by offering.' },
      ],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'items', label: 'Questions', type: 'list', listSchema: [
        { key: 'question', label: 'Question', type: 'text', required: true },
        { key: 'answer', label: 'Answer', type: 'textarea', required: true },
      ] },
    ],
  },
  FaqBlock
);

// ── GALLERY ─────────────────────────────────────────────────────────
register(
  {
    key: 'gallery',
    label: 'Image Gallery',
    description: 'Image gallery with thumbnails',
    category: 'media',
    icon: 'Images',
    defaultProps: {
      images: [],
      aspectRatio: '16/9',
    },
    schema: [
      { key: 'images', label: 'Images', type: 'list', listSchema: [
        { key: 'url', label: 'Image URL', type: 'image', required: true },
        { key: 'alt', label: 'Alt Text', type: 'text' },
        { key: 'caption', label: 'Caption', type: 'text' },
      ] },
      { key: 'aspectRatio', label: 'Aspect Ratio', type: 'select', options: [
        { label: '16:9', value: '16/9' },
        { label: '4:3', value: '4/3' },
        { label: '1:1', value: '1/1' },
      ], defaultValue: '16/9' },
    ],
  },
  GalleryBlock
);

// ── VIDEO ───────────────────────────────────────────────────────────
register(
  {
    key: 'video',
    label: 'Video Embed',
    description: 'Embed a YouTube or Vimeo video',
    category: 'media',
    icon: 'Play',
    defaultProps: { url: '', caption: '' },
    schema: [
      { key: 'url', label: 'Video URL', type: 'text', required: true, helpText: 'YouTube or Vimeo URL' },
      { key: 'caption', label: 'Caption', type: 'text' },
    ],
  },
  VideoBlock
);

// ── INVEST CTA ──────────────────────────────────────────────────────
register(
  {
    key: 'invest-cta',
    label: 'Invest Now CTA',
    description: 'Call-to-action button that triggers the investment flow',
    category: 'investment',
    icon: 'DollarSign',
    entityScoped: true,
    defaultProps: {
      label: 'Invest Now',
      style: 'primary',
      fullWidth: true,
      showMinimum: true,
      showAmountRaised: false,
    },
    schema: [
      { key: 'label', label: 'Button Label', type: 'text', defaultValue: 'Invest Now' },
      { key: 'style', label: 'Button Style', type: 'select', options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Outline', value: 'outline' },
        { label: 'Secondary', value: 'secondary' },
      ], defaultValue: 'primary' },
      { key: 'fullWidth', label: 'Full Width', type: 'boolean', defaultValue: true },
      { key: 'showMinimum', label: 'Show Minimum Investment', type: 'boolean', defaultValue: true },
      { key: 'showAmountRaised', label: 'Show Amount Raised', type: 'boolean', defaultValue: false },
    ],
  },
  InvestCtaBlock
);

// ── SUMMARY CARD ────────────────────────────────────────────────────
register(
  {
    key: 'summary-card',
    label: 'Investment Summary',
    description: 'Key investment terms in a summary card',
    category: 'investment',
    icon: 'ClipboardList',
    entityScoped: true,
    defaultProps: {
      heading: 'Investment Summary',
      items: [
        { label: 'Minimum Investment', value: '$1,000' },
        { label: 'Target Return', value: '12%' },
        { label: 'Term', value: '5 Years' },
      ],
      showProgress: true,
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Investment Summary' },
      { key: 'items', label: 'Summary Items', type: 'list', listSchema: [
        { key: 'label', label: 'Label', type: 'text', required: true },
        { key: 'value', label: 'Value', type: 'text', required: true },
      ] },
      { key: 'showProgress', label: 'Show Fundraising Progress', type: 'boolean', defaultValue: true },
    ],
  },
  SummaryCardBlock
);

// ── RISK DISCLOSURE ─────────────────────────────────────────────────
register(
  {
    key: 'risk-disclosure',
    label: 'Risk Disclosure',
    description: 'Expandable risk disclosure section',
    category: 'content',
    icon: 'AlertTriangle',
    defaultProps: {
      title: 'Important Risk Disclosures',
      description: 'All investments involve risk. Please review the offering documents carefully.',
      risks: [],
    },
    schema: [
      { key: 'title', label: 'Title', type: 'text', defaultValue: 'Important Risk Disclosures' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'risks', label: 'Risk Items', type: 'list', listSchema: [
        { key: 'text', label: 'Risk Description', type: 'textarea', required: true },
      ] },
    ],
  },
  RiskDisclosureBlock
);

// ── DOCUMENTS ───────────────────────────────────────────────────────
register(
  {
    key: 'documents',
    label: 'Documents',
    description: 'List of downloadable documents (auto-loads from entity)',
    category: 'content',
    icon: 'FileDown',
    entityScoped: true,
    defaultProps: { heading: 'Offering Documents' },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text', defaultValue: 'Offering Documents' },
    ],
  },
  DocumentsBlock
);

// ── TEAM ────────────────────────────────────────────────────────────
register(
  {
    key: 'team',
    label: 'Team / People',
    description: 'Team member cards with photo, name, title, and bio',
    category: 'content',
    icon: 'Users',
    defaultProps: {
      heading: 'Our Team',
      members: [],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'members', label: 'Team Members', type: 'list', listSchema: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'title', label: 'Title / Role', type: 'text' },
        { key: 'photoUrl', label: 'Photo URL', type: 'image' },
        { key: 'bio', label: 'Bio', type: 'textarea' },
      ] },
    ],
  },
  TeamBlock
);

// ── TESTIMONIALS ────────────────────────────────────────────────────
register(
  {
    key: 'testimonials',
    label: 'Testimonials',
    description: 'Investor or client testimonials',
    category: 'content',
    icon: 'Quote',
    defaultProps: {
      heading: 'What Investors Say',
      items: [],
    },
    schema: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'items', label: 'Testimonials', type: 'list', listSchema: [
        { key: 'quote', label: 'Quote', type: 'textarea', required: true },
        { key: 'author', label: 'Author Name', type: 'text', required: true },
        { key: 'role', label: 'Role / Title', type: 'text' },
        { key: 'imageUrl', label: 'Photo URL', type: 'image' },
      ] },
    ],
  },
  TestimonialsBlock
);

// ── PROGRESS BAR ────────────────────────────────────────────────────
register(
  {
    key: 'progress-bar',
    label: 'Progress / Fundraising',
    description: 'Visual progress bar with labels',
    category: 'data',
    icon: 'Activity',
    entityScoped: true,
    defaultProps: {
      heading: 'Fundraising Progress',
      raised: 0,
      target: 0,
      showPercentage: true,
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'raised', label: 'Amount Raised ($)', type: 'number', helpText: 'Leave 0 to auto-populate from entity' },
      { key: 'target', label: 'Target Amount ($)', type: 'number', helpText: 'Leave 0 to auto-populate from entity' },
      { key: 'showPercentage', label: 'Show Percentage', type: 'boolean', defaultValue: true },
    ],
  },
  ProgressBarBlock
);

// ── TABS ────────────────────────────────────────────────────────────
register(
  {
    key: 'tabs',
    label: 'Tabbed Content',
    description: 'Organize content into switchable tabs — supports text, documents, or location blocks as tab content',
    category: 'layout',
    icon: 'Layers',
    defaultProps: {
      tabs: [
        { label: 'Overview', contentType: 'text', content: 'Overview content here...' },
        { label: 'Documents', contentType: 'documents' },
        { label: 'Location', contentType: 'location' },
      ],
    },
    schema: [
      { key: 'tabs', label: 'Tabs', type: 'list', listSchema: [
        { key: 'label', label: 'Tab Label', type: 'text', required: true },
        {
          key: 'contentType',
          label: 'Content Type',
          type: 'select',
          defaultValue: 'text',
          options: [
            { label: 'Text / Rich Text', value: 'text' },
            { label: 'Multiple Blocks', value: 'blocks' },
            { label: 'Documents (entity-linked)', value: 'documents' },
            { label: 'Location Map (entity-linked)', value: 'location' },
          ],
        },
        { key: 'content', label: 'Tab Content', type: 'richtext', helpText: 'Used when Content Type is "Text"' },
        { key: 'blocks', label: 'Block Components (JSON)', type: 'json', helpText: 'Array of {blockKey, props} objects — used when Content Type is "Multiple Blocks"' },
      ] },
    ],
  },
  TabsBlock
);

// ── FUND TERMS ──────────────────────────────────────────────────────
register(
  {
    key: 'fund-terms',
    label: 'Fund Terms',
    description: 'Key-value table of fund terms and structure',
    category: 'investment',
    icon: 'Table',
    defaultProps: {
      heading: 'Fund Terms',
      items: [
        { term: 'Preferred Return', value: '8% Annual' },
        { term: 'Profit Split', value: '70/30 (LP/GP)' },
        { term: 'Minimum Investment', value: '$50,000' },
      ],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Fund Terms' },
      { key: 'items', label: 'Terms', type: 'list', listSchema: [
        { key: 'term', label: 'Term / Label', type: 'text', required: true },
        { key: 'value', label: 'Value', type: 'text', required: true },
      ] },
    ],
  },
  FundTermsBlock
);

// ── CASE STUDIES ─────────────────────────────────────────────────────
register(
  {
    key: 'case-studies',
    label: 'Case Studies / Track Record',
    description: 'Table of property case studies with financial metrics',
    category: 'investment',
    icon: 'BarChart2',
    defaultProps: {
      heading: 'Selected Case Studies',
      eyebrow: 'Track Record',
      disclaimer: 'Past performance does not guarantee future results.',
      rows: [],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Selected Case Studies' },
      { key: 'eyebrow', label: 'Eyebrow Text', type: 'text' },
      { key: 'disclaimer', label: 'Disclaimer', type: 'textarea' },
      { key: 'rows', label: 'Case Studies', type: 'list', listSchema: [
        { key: 'property', label: 'Property Name', type: 'text', required: true },
        { key: 'market', label: 'Market / Location', type: 'text', required: true },
        { key: 'acquisition', label: 'Acquisition Price', type: 'text', required: true },
        { key: 'revenue', label: 'Annual Revenue', type: 'text', required: true },
        { key: 'netCashFlow', label: 'Net Cash Flow', type: 'text', required: true },
        { key: 'irr', label: 'IRR', type: 'text', required: true },
      ] },
    ],
  },
  CaseStudiesBlock
);

// ── YIELD COMPARISON ─────────────────────────────────────────────────
register(
  {
    key: 'yield-comparison',
    label: 'Yield Comparison',
    description: 'Grid of yield comparison cards vs. other asset classes',
    category: 'investment',
    icon: 'TrendingUp',
    defaultProps: {
      heading: 'Average Annual Cash Yield Comparison',
      subheading: "Hive's primary differentiator: high-yield current income with quarterly distributions",
      cards: [
        { label: 'S&P 500', value: '1.5%', subtitle: 'Avg. dividend yield', isHighlight: false },
        { label: '10-Yr Treasury', value: '3%', subtitle: 'Fixed coupon', isHighlight: false },
        { label: 'Public REIT', value: '3.5%', subtitle: 'Avg. distribution yield', isHighlight: false },
        { label: 'Private REIT', value: '5%', subtitle: 'Avg. distribution yield', isHighlight: false },
        { label: 'Hive Fund', value: '12%', subtitle: 'Target annual cash yield · Quarterly distributions', isHighlight: true },
      ],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'subheading', label: 'Subheading', type: 'textarea' },
      { key: 'cards', label: 'Comparison Cards', type: 'list', listSchema: [
        { key: 'label', label: 'Asset Class Label', type: 'text', required: true },
        { key: 'value', label: 'Yield Value', type: 'text', required: true },
        { key: 'subtitle', label: 'Subtitle', type: 'text' },
        { key: 'isHighlight', label: 'Highlight Card', type: 'boolean', defaultValue: false },
      ] },
    ],
  },
  YieldComparisonBlock
);

// ── LOCATION ─────────────────────────────────────────────────────────
register(
  {
    key: 'location',
    label: 'Location Map',
    description: 'Google Maps embed using the property/offering address',
    category: 'content',
    icon: 'MapPin',
    entityScoped: true,
    defaultProps: {
      heading: 'Location',
      address: '',
      zoom: 14,
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Location' },
      { key: 'address', label: 'Address Override', type: 'text', helpText: 'Leave blank to use property address' },
      { key: 'zoom', label: 'Map Zoom Level', type: 'number', defaultValue: 14, min: 1, max: 20 },
    ],
  },
  LocationBlock
);

// ── SPACER ──────────────────────────────────────────────────────────
register(
  {
    key: 'spacer',
    label: 'Spacer',
    description: 'Vertical spacing between sections',
    category: 'layout',
    icon: 'Minus',
    defaultProps: { height: 'md' },
    schema: [
      { key: 'height', label: 'Height', type: 'select', options: [
        { label: 'Small (16px)', value: 'sm' },
        { label: 'Medium (32px)', value: 'md' },
        { label: 'Large (64px)', value: 'lg' },
        { label: 'Extra Large (96px)', value: 'xl' },
      ], defaultValue: 'md' },
    ],
  },
  SpacerBlock
);

// ── PROJECTION TABLE ────────────────────────────────────────────────
register(
  {
    key: 'projection-table',
    label: 'Projection Table',
    description: 'Year-by-year growth projection chart and data table',
    category: 'data',
    icon: 'TrendingUp',
    defaultProps: {
      heading: 'Year-by-Year Projection',
      initialInvestment: 100000,
      columns: [],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Year-by-Year Projection' },
      { key: 'body', label: 'Description', type: 'textarea' },
      { key: 'initialInvestment', label: 'Initial Investment', type: 'number', defaultValue: 100000 },
    ],
  },
  ProjectionTableBlock
);

// ── ASSUMPTIONS ─────────────────────────────────────────────────────
register(
  {
    key: 'assumptions',
    label: 'Assumptions',
    description: 'Benchmark assumption cards in a grid layout',
    category: 'data',
    icon: 'Info',
    defaultProps: {
      heading: 'Benchmark Assumptions',
      items: [],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Benchmark Assumptions' },
      { key: 'body', label: 'Description', type: 'textarea' },
    ],
  },
  AssumptionsBlock
);

// ── NAVBAR ──────────────────────────────────────────────────────────
register(
  {
    key: 'navbar',
    label: 'Navigation Bar',
    description: 'Top navigation with logo, links, and CTA button',
    category: 'marketing',
    icon: 'Menu',
    defaultProps: {
      links: [
        { label: 'Home', href: '/' },
        { label: 'About', href: '/about' },
        { label: 'Contact', href: '/contact' },
      ],
      ctaText: 'Get Started',
      ctaLink: '/register',
      variant: 'solid',
    },
    schema: [
      { key: 'variant', label: 'Style', type: 'select', options: [
        { label: 'Solid (white)', value: 'solid' },
        { label: 'Dark', value: 'dark' },
        { label: 'Transparent', value: 'transparent' },
      ], defaultValue: 'solid' },
      { key: 'logoUrl', label: 'Logo Image URL', type: 'image' },
      { key: 'ctaText', label: 'CTA Button Text', type: 'text', defaultValue: 'Get Started' },
      { key: 'ctaLink', label: 'CTA Button Link', type: 'text', defaultValue: '/register' },
      { key: 'links', label: 'Nav Links', type: 'list', listSchema: [
        { key: 'label', label: 'Label', type: 'text' },
        { key: 'href', label: 'Link', type: 'text' },
      ] },
    ],
  },
  NavbarBlock
);

// ── CTA BANNER ──────────────────────────────────────────────────────
register(
  {
    key: 'cta-banner',
    label: 'CTA Banner',
    description: 'Call-to-action section with heading, text, and buttons',
    category: 'marketing',
    icon: 'Megaphone',
    defaultProps: {
      heading: 'Ready to get started?',
      body: 'Join thousands of investors on our platform.',
      primaryButtonText: 'Get Started',
      primaryButtonLink: '/register',
      variant: 'dark',
      alignment: 'center',
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Body Text', type: 'textarea' },
      { key: 'primaryButtonText', label: 'Primary Button', type: 'text' },
      { key: 'primaryButtonLink', label: 'Primary Link', type: 'text' },
      { key: 'secondaryButtonText', label: 'Secondary Button', type: 'text' },
      { key: 'secondaryButtonLink', label: 'Secondary Link', type: 'text' },
      { key: 'variant', label: 'Style', type: 'select', options: [
        { label: 'Dark', value: 'dark' },
        { label: 'Light', value: 'light' },
        { label: 'Brand Color', value: 'brand' },
      ], defaultValue: 'dark' },
      { key: 'alignment', label: 'Alignment', type: 'select', options: [
        { label: 'Center', value: 'center' },
        { label: 'Left', value: 'left' },
      ], defaultValue: 'center' },
    ],
  },
  CtaBannerBlock
);

// ── PRICING ─────────────────────────────────────────────────────────
register(
  {
    key: 'pricing',
    label: 'Pricing Table',
    description: 'Pricing tiers with features and CTA buttons',
    category: 'marketing',
    icon: 'CreditCard',
    defaultProps: {
      heading: 'Pricing',
      body: 'Choose the plan that works for you.',
      tiers: [],
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text', defaultValue: 'Pricing' },
      { key: 'body', label: 'Subheading', type: 'textarea' },
      { key: 'tiers', label: 'Pricing Tiers', type: 'list', listSchema: [
        { key: 'name', label: 'Tier Name', type: 'text' },
        { key: 'price', label: 'Price', type: 'text' },
        { key: 'period', label: 'Period', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'buttonText', label: 'Button Text', type: 'text' },
        { key: 'buttonLink', label: 'Button Link', type: 'text' },
        { key: 'isPopular', label: 'Popular Badge', type: 'boolean' },
      ] },
    ],
  },
  PricingBlock
);

// ── CONTACT FORM ────────────────────────────────────────────────────
register(
  {
    key: 'contact-form',
    label: 'Contact Form',
    description: 'Lead capture form that creates contacts',
    category: 'marketing',
    icon: 'Mail',
    defaultProps: {
      heading: 'Get in Touch',
      body: "We'd love to hear from you.",
      submitText: 'Send Message',
      variant: 'default',
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Description', type: 'textarea' },
      { key: 'submitText', label: 'Button Text', type: 'text', defaultValue: 'Send Message' },
      { key: 'successMessage', label: 'Success Message', type: 'text', defaultValue: 'Thank you! We will be in touch soon.' },
      { key: 'variant', label: 'Layout', type: 'select', options: [
        { label: 'Centered', value: 'default' },
        { label: 'Split (info + form)', value: 'split' },
      ], defaultValue: 'default' },
    ],
  },
  ContactFormBlock
);

// ── LOGO CLOUD ──────────────────────────────────────────────────────
register(
  {
    key: 'logo-cloud',
    label: 'Logo Cloud',
    description: 'Partner/client logo display strip',
    category: 'marketing',
    icon: 'Award',
    defaultProps: {
      heading: 'Trusted By',
      logos: [],
      variant: 'grid',
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Subtext', type: 'text' },
      { key: 'variant', label: 'Layout', type: 'select', options: [
        { label: 'Grid', value: 'grid' },
        { label: 'Scroll', value: 'scroll' },
      ], defaultValue: 'grid' },
      { key: 'logos', label: 'Logos', type: 'list', listSchema: [
        { key: 'name', label: 'Name', type: 'text' },
        { key: 'imageUrl', label: 'Logo Image', type: 'image' },
      ] },
    ],
  },
  LogoCloudBlock
);

// ── NEWSLETTER ──────────────────────────────────────────────────────
register(
  {
    key: 'newsletter',
    label: 'Newsletter Signup',
    description: 'Email capture for newsletters',
    category: 'marketing',
    icon: 'Inbox',
    defaultProps: {
      heading: 'Stay updated',
      body: 'Subscribe to our newsletter for the latest updates.',
      buttonText: 'Subscribe',
      variant: 'inline',
      dark: false,
    },
    schema: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'body', label: 'Description', type: 'text' },
      { key: 'buttonText', label: 'Button Text', type: 'text', defaultValue: 'Subscribe' },
      { key: 'successMessage', label: 'Success Message', type: 'text', defaultValue: 'Thanks for subscribing!' },
      { key: 'variant', label: 'Layout', type: 'select', options: [
        { label: 'Inline', value: 'inline' },
        { label: 'Stacked', value: 'stacked' },
      ], defaultValue: 'inline' },
      { key: 'dark', label: 'Dark Background', type: 'boolean', defaultValue: false },
    ],
  },
  NewsletterBlock
);

// ── FOOTER ──────────────────────────────────────────────────────────
register(
  {
    key: 'footer',
    label: 'Footer',
    description: 'Page footer with link columns and copyright',
    category: 'marketing',
    icon: 'PanelBottom',
    defaultProps: {
      variant: 'columns',
      columns: [
        { heading: 'Company', links: [{ label: 'About', href: '/about' }, { label: 'Contact', href: '/contact' }] },
        { heading: 'Legal', links: [{ label: 'Privacy', href: '/privacy' }, { label: 'Terms', href: '/terms' }] },
      ],
    },
    schema: [
      { key: 'variant', label: 'Style', type: 'select', options: [
        { label: 'Columns', value: 'columns' },
        { label: 'Simple', value: 'simple' },
      ], defaultValue: 'columns' },
      { key: 'copyrightText', label: 'Copyright Text', type: 'text' },
      { key: 'columns', label: 'Link Columns', type: 'list', listSchema: [
        { key: 'heading', label: 'Column Heading', type: 'text' },
      ] },
    ],
  },
  FooterBlock
);

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

export function getBlockComponent(key: string): React.LazyExoticComponent<BlockComponent> | null {
  return registry.get(key)?.component ?? null;
}

export function getBlockEntry(key: string): BlockRegistryEntry | null {
  return registry.get(key)?.entry ?? null;
}

export function getAllBlocks(): BlockRegistryEntry[] {
  return Array.from(registry.values()).map(r => r.entry);
}

export function getBlocksByCategory(category: string): BlockRegistryEntry[] {
  return getAllBlocks().filter(b => b.category === category);
}

export function getCategories(): string[] {
  const cats = new Set(getAllBlocks().map(b => b.category));
  return Array.from(cats);
}
